/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

/** A single `Label: value` line from a trace item. `value` stays as raw
 *  markdown so links (e.g. the Workflow link) survive rendering. */
export type TraceField = { label: string; value: string };

export type TraceBlock = {
  /** Display id shown in the `[...]` part of the title link. */
  traceId: string;
  /** Original `okahu://...` href, used to deep-link into the trace. */
  href: string;
  /** Whatever fields the backend returned for this trace, in order. */
  fields: TraceField[];
};

/**
 * Parse the body of an `okahu:trace-list` fence into structured blocks.
 *
 * Each item looks like:
 *   1. **[<traceId>](okahu://.../traces/<id>)**
 *      Workflow: [`name`](okahu://workflows/name)
 *      Status: success
 *      Time: <start> - <end>
 *      Duration: 7270ms
 *
 * Field names are NOT hardcoded — every `Label: value` line is captured as-is,
 * so a query that returns extra fields (e.g. an eval label) renders with no
 * code change.
 */
export function parseTraceList(content: string): TraceBlock[] {
  return content
    .trim()
    .split(/\n(?=\d+\.\s)/)
    .map((raw) => raw.trim())
    .filter(Boolean)
    .map((block) => {
      const [titleLine, ...rest] = block.split('\n');
      const linkMatch = titleLine.match(/\[([^\]]+)\]\(([^)]+)\)/);
      const traceId =
        linkMatch?.[1]?.trim() ??
        titleLine
          .replace(/^\d+\.\s*/, '')
          .replace(/\*/g, '')
          .trim();
      const href = linkMatch?.[2]?.trim() ?? '';

      const fields: TraceField[] = [];
      for (const rawLine of rest) {
        const line = rawLine.trim();
        if (!line) continue;
        // Non-greedy label stops at the FIRST colon, so values that contain
        // colons (timestamps in "Time: ...") keep them intact.
        const fieldMatch = line.match(/^([A-Za-z][\w /-]*?):\s*(.*)$/);
        if (fieldMatch) {
          fields.push({
            label: fieldMatch[1].trim(),
            value: fieldMatch[2].trim(),
          });
        } else if (fields.length) {
          // A wrapped continuation line for the previous field.
          fields[fields.length - 1].value += `\n${line}`;
        }
      }

      return { traceId, href, fields: fields.filter((f) => f.value) };
    })
    .filter((block) => block.fields.length > 0);
}

/** True while a streamed fence has no complete numbered item yet. */
export function isTraceListIncomplete(content: string): boolean {
  const text = content.trim();
  if (!text) return true;
  return text.split(/\n(?=\d+\.\s)/).every((part) => !part.trim());
}
